import {
  Description,
  People,
  Receipt,
  Payment,
  Assignment,
  AccountBalance,
  EventNote,
  LocalShipping,
  Assessment,
  WhatsApp,
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';

export type ReportCategory = 'operations' | 'hr' | 'finance' | 'admin' | 'all';

export type BuiltInReportType = 'requests' | 'employees' | 'invoices' | 'payrolls';

export interface ReportCatalogItem {
  id: string;
  titleKey: string;
  descriptionKey: string;
  category: Exclude<ReportCategory, 'all'>;
  icon: SvgIconComponent;
  color: string;
  /** Built-in reports open in the viewer; external reports navigate to path */
  builtInType?: BuiltInReportType;
  path?: string;
  exportFormats: ('pdf' | 'excel' | 'csv')[];
  keywords: string[];
}

export const REPORT_CATEGORIES: { id: ReportCategory; labelKey: string }[] = [
  { id: 'all', labelKey: 'reports.categories.all' },
  { id: 'operations', labelKey: 'reports.categories.operations' },
  { id: 'hr', labelKey: 'reports.categories.hr' },
  { id: 'finance', labelKey: 'reports.categories.finance' },
  { id: 'admin', labelKey: 'reports.categories.admin' },
];

export const BUILT_IN_REPORTS: ReportCatalogItem[] = [
  {
    id: 'requests',
    titleKey: 'reports.serviceRequests',
    descriptionKey: 'reports.viewRequests',
    category: 'operations',
    icon: Description,
    color: '#1976d2',
    builtInType: 'requests',
    exportFormats: ['pdf', 'excel'],
    keywords: ['service', 'request', 'operations', 'insurance', 'completed'],
  },
  {
    id: 'employees',
    titleKey: 'reports.employeeReport',
    descriptionKey: 'reports.employeeStatistics',
    category: 'hr',
    icon: People,
    color: '#2e7d32',
    builtInType: 'employees',
    exportFormats: ['pdf', 'excel'],
    keywords: ['employee', 'hr', 'department', 'staff'],
  },
  {
    id: 'invoices',
    titleKey: 'reports.invoices',
    descriptionKey: 'reports.invoicesDesc',
    category: 'finance',
    icon: Receipt,
    color: '#ed6c02',
    builtInType: 'invoices',
    exportFormats: ['pdf', 'excel'],
    keywords: ['invoice', 'billing', 'extra km', 'insurance'],
  },
  {
    id: 'payrolls',
    titleKey: 'reports.payrollReport',
    descriptionKey: 'reports.payrollSummaries',
    category: 'hr',
    icon: Payment,
    color: '#0288d1',
    builtInType: 'payrolls',
    exportFormats: ['pdf', 'excel'],
    keywords: ['payroll', 'salary', 'hr', 'wages'],
  },
];

export const EXTERNAL_REPORTS: ReportCatalogItem[] = [
  {
    id: 'service-request-report',
    titleKey: 'navigation.serviceRequestReport',
    descriptionKey: 'reports.serviceRequestReportDesc',
    category: 'operations',
    icon: Assignment,
    color: '#5c6bc0',
    path: '/reports/service-requests',
    exportFormats: ['pdf'],
    keywords: ['insurance', 'monthly', 'company'],
  },
  {
    id: 'service-provider-report',
    titleKey: 'navigation.serviceProviderReport',
    descriptionKey: 'reports.providerPerformance',
    category: 'operations',
    icon: LocalShipping,
    color: '#7b1fa2',
    path: '/service-providers/report',
    exportFormats: ['pdf', 'excel'],
    keywords: ['provider', 'performance', 'schedule'],
  },
  {
    id: 'request-items-report',
    titleKey: 'navigation.requestItemsReport',
    descriptionKey: 'reports.requestItemsDesc',
    category: 'operations',
    icon: Description,
    color: '#00838f',
    path: '/requests/report',
    exportFormats: ['pdf', 'excel'],
    keywords: ['items', 'parts', 'requests'],
  },
  {
    id: 'leave-report',
    titleKey: 'reports.leaveReport',
    descriptionKey: 'reports.leaveReportDesc',
    category: 'hr',
    icon: EventNote,
    color: '#c2185b',
    path: '/leave-requests/report',
    exportFormats: ['pdf', 'excel'],
    keywords: ['leave', 'vacation', 'annual', 'balance'],
  },
  {
    id: 'financial-reports',
    titleKey: 'reports.financialReports',
    descriptionKey: 'reports.financialReportsDesc',
    category: 'finance',
    icon: AccountBalance,
    color: '#1565c0',
    path: '/financial-reports',
    exportFormats: ['pdf'],
    keywords: ['balance sheet', 'income', 'trial balance', 'accounting'],
  },
  {
    id: 'tax-report',
    titleKey: 'navigation.taxReport',
    descriptionKey: 'reports.taxReportDesc',
    category: 'finance',
    icon: Assessment,
    color: '#6d4c41',
    path: '/finance/tax',
    exportFormats: ['pdf', 'excel'],
    keywords: ['tax', 'vat', 'finance'],
  },
  {
    id: 'whatsapp-reports',
    titleKey: 'reports.whatsappReports',
    descriptionKey: 'reports.whatsappReportsDesc',
    category: 'admin',
    icon: WhatsApp,
    color: '#25D366',
    path: '/operations/whatsapp-chat',
    exportFormats: ['pdf'],
    keywords: ['whatsapp', 'messages', 'analytics'],
  },
];

export const ALL_REPORTS: ReportCatalogItem[] = [...BUILT_IN_REPORTS, ...EXTERNAL_REPORTS];

export const BUILT_IN_BY_TYPE: Record<BuiltInReportType, ReportCatalogItem> = Object.fromEntries(
  BUILT_IN_REPORTS.map((r) => [r.builtInType!, r])
) as Record<BuiltInReportType, ReportCatalogItem>;
